<template>
  <div class="agencies-container">
    <TableFiltersContainer padding="2px 0px 12px 0px">
      <AwTextField
        v-model="missionState.selectedFilters.search"
        leading-icon="i-ph-magnifying-glass"
        placeholder="Rechercher une commande"
        class="min-w-fit"
        :debounce-delay="200"
      />
      <BusinessUnitSelect v-model:business-unit-ids="missionState.selectedFilters.businessUnitIds" />
      <TableStatusSelect
        v-model:status="missionState.selectedFilters.status"
        :status-options="[
          { label: 'Ouverte', value: 'open' },
          { label: 'Fermée', value: 'closed' },
        ]"
      />
      <TableFiltersResetButton @click="resetFilters" />
    </TableFiltersContainer>
    <TableInteractive
      :key="tableKey"
      v-model:sorting="missionState.sorting"
      :loading="missionState.loading"
      :enable-sorting="true"
      :manual-sorting="true"
      :data="missions"
      :columns="tableColumns"
      :empty-state-type="emptyStateType"
      :empty-state-reset-filters="resetFilters"
      height="100%"
      :on-select="(row: any) => navigateTo(`/missions/${row.original.id}`, { open: { target: '_blank' } })"
    >
      <template #status-cell="{ row }">
        <TableCellContainer :is-loading="missionState.loading" :skeletons="['text']">
          <AwBadge :color="row.original.status === 'open' ? 'green' : 'red'">
            {{ row.original.status === "open" ? "Ouverte" : "Fermée" }}
          </AwBadge>
        </TableCellContainer>
      </template>
      <template #start_date-cell="{ row }">
        <TableCellContainer :is-loading="missionState.loading" :skeletons="['text']">
          <TableCellDate v-if="row.original.start_date" :date="row.original.start_date" />
          <div v-else>-</div>
        </TableCellContainer>
      </template>
      <template #job_name-cell="{ row }">
        <TableCellContainer :is-loading="missionState.loading" :skeletons="['text']">
          <TextEllipsis>
            {{ row.original.job_name }}
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #address-cell="{ row }">
        <TableCellContainer :is-loading="missionState.loading" :skeletons="['text']">
          <TextEllipsis>
            {{
              row.original.address?.postal_code && row.original.address?.city
                ? `${row.original.address?.postal_code} - ${row.original.address?.city}`
                : `${row.original.address?.postal_code || row.original.address?.city || "-"}`
            }}
          </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #owner-cell="{ row }">
        <TableCellContainer :is-loading="missionState.loading" :skeletons="['text']">
          <TableCellTeamMember :team-member="row.original.owner" />
        </TableCellContainer>
      </template>
      <template #business_unit-cell="{ row }">
        <TableCellContainer :is-loading="missionState.loading" :skeletons="['text']">
          <TextEllipsis> {{ row.original.business_unit?.emoji }} {{ row.original.business_unit?.name }} </TextEllipsis>
        </TableCellContainer>
      </template>
      <template #created_at-cell="{ row }">
        <TableCellContainer :is-loading="missionState.loading" :skeletons="['text']">
          <TableCellDate :date="row.original.created_at" format="distanceToNow" />
        </TableCellContainer>
      </template>
      <template #footer>
        <TablePagination
          :total-items="missionState.totalItems"
          :page-size="missionState.selectedFilters.pageSize"
          :page-index="missionState.selectedFilters.pageIndex"
          @update:page-size="missionState.selectedFilters.pageSize = $event"
          @update:page-index="missionState.selectedFilters.pageIndex = $event"
        />
      </template>
    </TableInteractive>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "~/composables/useToast";
import { useMissions } from "~/components/mission/composables/useMissions";
import type { TableEmptyStateType } from "~/utils/types";
import { useTableColumn } from "~/composables/useTableColumn";
import type { MissionSchema } from "@asap/shared/schemas/mission/list";

const props = defineProps<{
  companyId: string;
}>();

const missions = ref<MissionSchema[]>([]);

const emptyStateType = ref<TableEmptyStateType>("no-data");

const { addToastError } = useToast();
const { missionState, selectMultipleForTable, convertSortingToOrders } = useMissions();

const { tableKey, tableColumns } = useTableColumn(
  [
    {
      label: "Statut",
      value: {
        accessorKey: "status",
        header: "Statut",
        maxSize: 60,
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Début",
      value: {
        accessorKey: "start_date",
        header: "Début",
        maxSize: 100,
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Poste",
      value: {
        accessorKey: "job_name",
        header: "Poste",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Lieux",
      value: {
        accessorKey: "address",
        header: "Lieux",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Consultant",
      value: {
        accessorKey: "owner",
        header: "Consultant",
      },
      blocked: false,
      selected: true,
    },
    {
      label: "Business Unit",
      value: {
        accessorKey: "business_unit",
        header: "Business Unit",
      },
      blocked: true,
      selected: true,
    },
    {
      label: "Création",
      value: {
        accessorKey: "created_at",
        header: "Création",
      },
      blocked: true,
      selected: true,
    },
  ],
  "company-insurances-table-columns"
);

const resetFilters = () => {
  missionState.selectedFilters.search = undefined;
  missionState.selectedFilters.status = undefined;
  missionState.selectedFilters.businessUnitIds = undefined;
  missionState.sorting = [{ id: "created_at", desc: true }];
};

const query = async function () {
  try {
    const orders = convertSortingToOrders(missionState.sorting);
    const { data, count } = await selectMultipleForTable({
      ...missionState.selectedFilters,
      companyId: props.companyId,
      orders,
    });
    if (data) missions.value = data;
    if (count !== null) missionState.totalItems = count;
    if (missionState.selectedFilters.search) {
      emptyStateType.value = "no-data-with-filters";
    } else {
      emptyStateType.value = "no-data";
    }
  } catch (error) {
    emptyStateType.value = "error";
    addToastError(
      {
        title: "Une erreur est survenue lors de la récupération des missions",
      },
      error
    );
  }
};

const isInitialized = ref(false);

onMounted(async () => {
  await query();
  isInitialized.value = true;
});

watch(() => missionState.selectedFilters.pageIndex, query);

watch(
  () => [
    missionState.sorting,
    missionState.selectedFilters.search,
    missionState.selectedFilters.pageSize,
    missionState.selectedFilters.status,
    missionState.selectedFilters.companyId,
    missionState.selectedFilters.orders,
    missionState.selectedFilters.businessUnitIds,
  ],
  async () => {
    if (!isInitialized.value) return;
    missionState.selectedFilters.pageIndex = 0;
    await query();
  }
);
</script>

<style scoped lang="scss">
.agencies-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
}

.toggle_wrapper {
  margin: 0 auto;
  margin-top: 1rem;
}
</style>
